/** The platform's app registry: what apps exist, their modules, and the ability gating each. Nav, routing and the "is it built" flag all read this file.
 *  Every module lives under its app prefix (`/finance/sales`), which stops two apps fighting over a generic path. `status`: 'live' built; 'preview' on stub data; 'planned' shown disabled. */
import {
  Armchair,
  BarChart3,
  CircleUser,
  Building2,
  Bus,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  Clock,
  FileText,
  Gauge,
  HardDrive,
  HardHat,
  Landmark,
  LayoutDashboard,
  History,
  Network,
  Receipt,
  Settings2,
  Shield,
  Shirt,
  Tag,
  Users,
  Wallet,
  Workflow,
  type LucideIcon,
} from 'lucide-react'

type AppStatus = 'live' | 'preview' | 'planned'

/** The bands the sidebar groups apps into: by what you are doing, not by which Power App the pages came from. */
type NavGroup = 'info' | 'work' | 'finance' | 'admin'

export const NAV_GROUPS: { key: NavGroup; labelKey: string }[] = [
  { key: 'info', labelKey: 'nav.groupInfo' },
  { key: 'work', labelKey: 'nav.groupWork' },
  { key: 'finance', labelKey: 'nav.groupFinance' },
  { key: 'admin', labelKey: 'nav.groupAdmin' },
]

interface ModuleDef {
  /** Path segment below the app prefix. '' is the app's own landing. */
  path: string
  /** i18n key for the label. */
  labelKey: string
  icon: LucideIcon
  /** Ability required to see it; omitted means any signed-in user. */
  ability?: string
  /** Show only to a roster role (`rosterVisible`) or a roster manager (`isRosterManager`): per-request server facts, not abilities. */
  requires?: 'rosterRole' | 'rosterManager'
  /** With `ability`: a roster manager passes too, for a page the server opens to both. */
  orRosterManager?: boolean
  /** `reader`: `/auth/me.inventory_reader`. `gear`: the assets tick or a roster manager. Per-request server facts, not abilities. */
  inventoryGate?: InventoryGate
}

export type InventoryGate = 'reader' | 'gear'

/** True when the viewer may see something gated this way.
 *  There is no `adminOnly`: a tier is not divisible, so every page is gated on an ability and an admin passes all of them; add an ability instead of reviving the flag.
 *  `anyAbility` is the OR case for a container whose pages are granted separately; each page inside keeps its own gate. */
export function isVisible(
  gate: {
    ability?: string
    anyAbility?: string[]
    requires?: 'rosterRole' | 'rosterManager' | 'contractorLog' | 'rosterSchedule'
    orRosterManager?: boolean
    inventoryGate?: InventoryGate
  },
  holds: (ability: string) => boolean,
  /** `rosterVisible(user)`. Defaults to FALSE so a gate called without it hides rather than shows. */
  hasRosterRole = false,
  /** `isRosterManager(user)`. Defaults to FALSE for the same reason. */
  isRosterManager = false,
  /** `/auth/me.contractor_log`. Defaults to FALSE for the same reason. */
  hasContractorLog = false,
  /** `scheduleVisible(user)`. Defaults to FALSE for the same reason. */
  hasRosterSchedule = false,
  /** `/auth/me.inventory_reader` and the assets tick. Default to FALSE for the same reason. */
  inventory: { reader: boolean; assets: boolean } = { reader: false, assets: false },
): boolean {
  if (gate.inventoryGate === 'reader' && !inventory.reader) return false
  if (gate.inventoryGate === 'gear' && !inventory.assets && !isRosterManager) return false
  if (gate.requires === 'rosterRole' && !hasRosterRole) return false
  if (gate.requires === 'rosterSchedule' && !hasRosterSchedule) return false
  if (gate.requires === 'contractorLog' && !hasContractorLog) return false
  if (gate.requires === 'rosterManager' && !isRosterManager) return false
  if (gate.ability && !holds(gate.ability) && !(gate.orRosterManager && isRosterManager)) return false
  if (gate.anyAbility && !gate.anyAbility.some(holds)) return false
  return true
}

/** ⛔ The share-link viewer's whole rail: the server answers it on the company dashboard and calendar only. */
export const PUBLIC_APP_KEYS: readonly string[] = ['dashboard']

/** The apps a public-only viewer sees, in rail order; every other app is hidden and unrouted for them. */
export function publicApps<T extends { key: string }>(apps: readonly T[]): T[] {
  return apps.filter((app) => PUBLIC_APP_KEYS.includes(app.key))
}

export interface AppDef {
  key: string
  /** URL prefix, without a trailing slash. */
  prefix: string
  /** Further URL prefixes this app owns, so the sidebar highlights it there. */
  alsoPrefixes?: string[]
  labelKey: string
  icon: LucideIcon
  status: AppStatus
  /** Required, so a new app cannot quietly land at the end without a decision. */
  group: NavGroup
  /** Which Power App this replaces; shown in the placeholder pages. */
  replaces?: string
  /** Hide the whole app unless the user holds this ability; grantable to one person without making them an administrator. */
  ability?: string
  /** Show the app to an admin, or to anyone holding ANY of these; see `isVisible`. */
  anyAbility?: string[]
  /** Show only when the server reports it (`/auth/me.contractor_log`, `roster_access.schedule`); a per-request fact, so it cannot be granted on /admin/users. */
  requires?: 'contractorLog' | 'rosterSchedule'
  inventoryGate?: InventoryGate
  /** Empty for a single-page app whose actions all open as popups. */
  modules: ModuleDef[]
}

export const APPS: AppDef[] = [
  // The dashboard is the first entry of the Info band. Prefix `/` matches only `/` itself in `appForPath`. No ability: everyone signed in lands here.
  {
    key: 'dashboard',
    prefix: '/',
    group: 'info',
    labelKey: 'app.dashboard',
    icon: LayoutDashboard,
    status: 'live',
    // `basic` makes the gate say what the API enforces; the `roster.self`-only principal never reaches the shell.
    ability: 'basic',
    modules: [],
  },
  // Finance is a band, not an app: its pages are peers and keep their `/finance/...` URLs, which stops two apps fighting over `/requests` and `/trips`.
  // Clients and the price list are pages of Sales, opened only on the way to or from a sale; their old URLs redirect (App.tsx).
  {
    key: 'sales',
    prefix: '/finance/sales',
    group: 'finance',
    labelKey: 'app.sales',
    icon: FileText,
    status: 'live',
    replaces: 'TRG Finance (SALES solution)',
    // Granted separately, so the tab opens for anyone holding any of them and each page keeps its own gate. `payroll` is included for the Banks tab.
    anyAbility: ['staff', 'payroll'],
    modules: [
      // '' is the app's own landing: the documents list at /finance/sales.
      { path: '', labelKey: 'app.salesDocuments', icon: FileText, ability: 'staff' },
      // Building2 rather than Users: HR is Users, and two identical glyphs in a rail of bare icons are two coin flips.
      { path: 'clients', labelKey: 'app.clients', icon: Building2, ability: 'staff' },
      { path: 'price-list', labelKey: 'app.priceList', icon: Tag, ability: 'staff' },
      // The company bank list; same gate as `App.tsx`'s route and every write in `routers/bank.py`.
      { path: 'banks', labelKey: 'bank.nav', icon: Landmark, ability: 'payroll' },
    ],
  },
  {
    key: 'activityexpense',
    prefix: '/finance/activity-expense',
    group: 'finance',
    labelKey: 'app.activityExpense',
    icon: Wallet,
    status: 'live',
    ability: 'staff',
    modules: [],
  },
  {
    key: 'requests',
    prefix: '/finance/requests',
    group: 'finance',
    labelKey: 'app.requests',
    icon: Receipt,
    status: 'live',
    ability: 'staff',
    modules: [],
  },
  {
    key: 'time',
    prefix: '/time',
    group: 'work',
    labelKey: 'app.timeLogging',
    icon: Clock,
    status: 'live',
    replaces: 'Time Management’s TIME LOGGING / TIME DETAILS screens',
    // Top level and a single page: logging is a daily habit, and nesting it a level down would cost the most repeated action an extra click. Corrections live in this page's admin scope.
    ability: 'self.log',
    modules: [],
  },
  {
    key: 'contractorLog',
    prefix: '/contractor-log',
    group: 'work',
    labelKey: 'app.contractorLog',
    icon: ClipboardList,
    status: 'live',
    // Recorders and supervisors hold no ability, so the gate is the server's fact rather than a grant.
    requires: 'contractorLog',
    modules: [],
  },
  {
    key: 'leave',
    prefix: '/leave',
    group: 'work',
    labelKey: 'app.leave',
    icon: CalendarDays,
    status: 'live',
    replaces: 'Time Management’s ANNUAL LEAVE REQUEST / LEAVE CALENDAR screens',
    // One page: request-a-leave and the calendar are the same records read differently (scope switch and list/calendar toggle). Corrections are a scope of this page, not a route.
    ability: 'self.log',
    modules: [],
  },
  {
    key: 'hotdesk',
    prefix: '/hot-desk',
    group: 'work',
    labelKey: 'app.hotDesk',
    icon: Armchair,
    status: 'live',
    replaces: 'Time Management’s HOT DESK / RESERVATION LIST screens',
    // Top level and a single page: booking a desk is something every employee does on its own.
    ability: 'self.log',
    modules: [],
  },
  // My Panel: the signed-in person's own data. Gated on `basic`; every `/api/me/*` route reads the person off the session.
  // One page (`pages/me/MyPanel.tsx`); the old tab URLs redirect (`legacyMePath`). `modules: []` on purpose.
  {
    key: 'me',
    prefix: '/me',
    group: 'info',
    labelKey: 'app.me',
    icon: CircleUser,
    status: 'live',
    replaces: 'the payslip that used to arrive by email',
    // `basic` is the gate of the backend `/api/me` router.
    ability: 'basic',
    modules: [],
  },
  {
    key: 'roster',
    prefix: '/roster',
    group: 'work',
    labelKey: 'app.roster',
    icon: CalendarRange,
    // Live: RosterSection reads /api/roster and saves it back with optimistic concurrency.
    status: 'live',
    replaces: 'Roster app + Time Management’s guest section',
    // Every member reads Schedule, read-only, as the manager sees it; every other page needs a roster role.
    requires: 'rosterSchedule',
    modules: [
      { path: 'schedule', labelKey: 'app.schedule', icon: CalendarRange },
      { path: 'people', labelKey: 'app.rosterPeople', icon: Users, requires: 'rosterRole' },
      // Setup ("Projects") is gated, unlike its five read-only siblings, which the server filters to the staffer's project(s). It is all edit surfaces, so it is hidden rather than shown disabled;
      // `Setup.tsx` repeats the check for a direct URL. The roster MANAGER only: `roster.plan` off the roster plans nothing, so gating on it would show Setup to people the server refuses.
      { path: 'projects', labelKey: 'app.rosterSetup', icon: Settings2, requires: 'rosterManager' },
      { path: 'bus', labelKey: 'app.bus', icon: Bus, requires: 'rosterRole' },
      { path: 'hse', labelKey: 'app.hse', icon: HardHat, requires: 'rosterRole' },
      // The same rows and API as Inventory's Roster view.
      { path: 'gear', labelKey: 'inventory.register.gear', icon: Shirt, inventoryGate: 'gear', requires: 'rosterRole' },
      { path: 'performance', labelKey: 'app.performance', icon: Gauge, requires: 'rosterRole' },
      { path: 'cost', labelKey: 'app.cost', icon: Wallet, requires: 'rosterRole' },
      // One's own days: everyone who reaches the roster, and it is a self-service login's whole app.
      { path: 'my', labelKey: 'app.rosterMySchedule', icon: CalendarDays },
    ],
  },
  // No "Company" page: Org chart and Inventory are their own entries and the old `/company/...` URLs redirect (`legacyCompanyPath`).
  // Organization: Org chart and HR are one page with tabs, open to every member of staff; the HR tabs and columns appear only for `hr.view`, and every record and salary read keeps its own server gate.
  {
    key: 'organization',
    prefix: '/organization',
    alsoPrefixes: ['/hr'],
    group: 'info',
    labelKey: 'app.organization',
    icon: Network,
    status: 'live',
    replaces: 'HR app',
    modules: [],
  },
  // Every Full Time employee and anyone appointed reads; each tab's tick writes. One page, so no modules.
  {
    key: 'inventory',
    prefix: '/inventory',
    group: 'work',
    labelKey: 'app.inventory',
    icon: HardDrive,
    status: 'live',
    replaces: 'TRG Finance’s Laptop Registration screen',
    inventoryGate: 'reader',
    modules: [],
  },
  {
    key: 'payroll',
    prefix: '/payroll',
    group: 'finance',
    labelKey: 'app.payroll',
    icon: BarChart3,
    status: 'live',
    replaces: 'Salary Calculator app',
    // No modules on purpose: payroll is one page and everything opens as a popup over it. `anyAbility` because the route admits `salary.view` (rates and runs) or `admin` (access log);
    // who holds them is decided in backend core/permissions.py. Any admin sees the entry regardless, via the `isVisible` bypass.
    modules: [],
    anyAbility: ['salary.view', 'admin'],
  },
  // Administration is a band, not an app, gated per page by ability so HR can hold part of the band (leave, time logs, calendar) without touching platform accounts.
  // ⛔ Nav and routes must agree: each route in App.tsx carries the matching guard, since a tab hidden here but reachable by URL is the failure this pairing prevents.
  // Each page keeps its `/admin/...` prefix (bookmarked); `/admin` itself resolves through `bandLanding`.
  {
    key: 'adminusers',
    prefix: '/admin/users',
    group: 'admin',
    // "Users", not "Accounts": the page lists the people who may sign in.
    labelKey: 'app.users',
    icon: Shield,
    status: 'live',
    // The admin section (Users, Config, Audit, Automation) is admin only. Gated on the `admin` ability, not a revived `adminOnly` flag, so it stays grantable; same gate as the route in App.tsx.
    ability: 'admin',
    modules: [],
  },
  // One page at `/admin/config` with several tabs. ⛔ Must stay in step with `App.tsx`'s guard for the route: narrower hides a page from someone allowed, wider offers a dead-end link.
  // `anyAbility` is the OR of what any one tab needs; `Config.tsx` re-gates each tab.
  {
    key: 'adminconfig',
    prefix: '/admin/config',
    group: 'admin',
    labelKey: 'org.navConfig',
    icon: Settings2,
    status: 'live',
    anyAbility: ['admin', 'calendar.edit'],
    modules: [],
  },
  // No `adminauditlog` entry: the payroll access log is a tab inside `/payroll`, so a nav entry would be a dead link. The `app.adminAuditLog` label key survives for that tab.
  {
    key: 'adminaudit',
    prefix: '/admin/audit',
    group: 'admin',
    // The one audit entry: `/admin/audit` is the merged page (`Audit.tsx`); `app.adminAudit` is its label, and `app.adminChanges`/`app.adminPersonTrail` label its tabs.
    labelKey: 'app.adminAudit',
    icon: History,
    status: 'live',
    // Admin section, admin only, as is the API.
    ability: 'admin',
    modules: [],
  },
  // No `adminchanges` or `adminpersontrail` entry: both are tabs of `adminaudit`, and the nav resolvers match on a path prefix, so a `?tab=` entry could never highlight.
  // The old label keys survive: `Audit.tsx` and `AdminActions.tsx` still render them.
  {
    key: 'adminautomation',
    prefix: '/admin/automation',
    group: 'admin',
    labelKey: 'app.adminAutomation',
    icon: Workflow,
    status: 'live',
    ability: 'admin',
    modules: [],
  },
  // No `adminbanks` entry: the bank list is `adminconfig`'s tab, gated by `salary.edit` in its `anyAbility`. No `adminorganization` or `adminotp` entry either, for the same prefix-matching reason as `adminaudit`.
]

/** Where a bare band prefix (`/finance`, `/admin`) should send this viewer: the first app under the prefix, in sidebar order, that they may open.
 *  Not `firstVisibleModule`, which answers about modules inside one app. Pass the already-filtered visible apps; the dashboard is the fallback. */
export function bandLanding(visibleApps: AppDef[], prefix: string): string {
  return visibleApps.find((a) => a.prefix.startsWith(`${prefix}/`))?.prefix ?? '/'
}

/** The app that owns a pathname (the `dashboard` entry for `/`). */
export function appForPath(pathname: string): AppDef | undefined {
  return APPS.find((a) =>
    [a.prefix, ...(a.alsoPrefixes ?? [])].some((p) => pathname === p || pathname.startsWith(`${p}/`)),
  )
}

/** Full route for a module, e.g. finance + 'sales' -> '/finance/sales'. */
export function modulePath(app: AppDef, module: ModuleDef): string {
  return module.path ? `${app.prefix}/${module.path}` : app.prefix
}

/** Where an app's own prefix should land: the first module this viewer may open, or undefined if none (the caller turns that into the dashboard). */
export function firstVisibleModule(
  app: AppDef,
  holds: (ability: string) => boolean,
  hasRosterRole = false,
  isRosterManager = false,
): string | undefined {
  const module = app.modules.find((m) => isVisible(m, holds, hasRosterRole, isRosterManager))
  return module ? modulePath(app, module) : undefined
}
